import { Text } from 'bold-ui'
import { usePecField } from 'components/form'
import { parseISO } from 'date-fns'
import { useAcompanhamentoPreNatalQuery } from 'graphql/hooks.generated'
import React, { useMemo } from 'react'
import { calcularIMC } from 'util/atendimento'
import { meta } from 'view/atendimentos/atendimento-individual/model'
import { MedicaoAntropometricaPreNatal } from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/model-medicoesPreNatal'
import {
  getMedicaoForImcReferencia,
  getMedicoesAntropometricasPreNatal,
} from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/util-medicoesPreNatal'
import { PeriodoGestacaoModel } from 'view/atendimentos/detail/components/modals/types/PeriodoGestacaoModel'
import { CidadaoMedicoes } from 'view/atendimentos/detail/soap/aside/medicoes/model'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { TipoPreNatal } from '../../pre-natal/model-preNatal'
import { AccordionCardSection } from '../components/AccordionCardSection'
import { ContentCardSection } from '../components/ContentCardSection'
import { SubtitleCard } from '../components/SubtitleCard'
import { usePeriodoGestacional } from '../pre-natal/hooks/usePeriodoGestacional'
import { getDumAtual } from '../pre-natal/util/util-acompanhamentoPreNatal'
import { GrupoMedicoesTipoModel } from '../types/MedicaoModel'
import { MedicoesListCard } from './components/MedicoesListCard'
import { ImcPreGestacionalCardItem } from './ImcPreGestacionalCardItem'

export interface MedicoesCardSoapPageProps {
  loading: boolean
  prontuarioId: ID
  dataAtendimento?: Instant
  historicoMedicoes: HistoricoMedicaoModel[]
  medicoesHoje: ReadonlyArray<GrupoMedicoesTipoModel>
  medicoesAnterioresExpandido: ReadonlyArray<GrupoMedicoesTipoModel>
  medicoesAnterioresAccordion: ReadonlyArray<GrupoMedicoesTipoModel>
  canRender: boolean
  cidadao: CidadaoMedicoes
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  isGestante: boolean
  dumAtendimentoAtual?: LocalDate
  tipoPreNatal?: TipoPreNatal
}

export function MedicoesCardSoapPage(props: Readonly<MedicoesCardSoapPageProps>) {
  const {
    loading,
    prontuarioId,
    dataAtendimento,
    historicoMedicoes,
    medicoesHoje,
    medicoesAnterioresExpandido,
    medicoesAnterioresAccordion,
    canRender,
    cidadao,
    gestacoes,
    isGestante,
    dumAtendimentoAtual,
    tipoPreNatal,
  } = props

  const {
    data: { atendimentosProfUltimaGestacao },
    loading: loadingAcompanhamentoPreNatal,
  } = useAcompanhamentoPreNatalQuery({
    variables: { input: prontuarioId },
  })

  const dumPersistida = getDumAtual(atendimentosProfUltimaGestacao)
  const dumAtendimentoAtualParsed = dumAtendimentoAtual && parseISO(dumAtendimentoAtual)

  const { dataInicioGestacao, loading: loadingPeriodoGestacional } = usePeriodoGestacional({
    prontuarioId,
    dum: dumPersistida,
    dumAtendimentoAtual: dumAtendimentoAtualParsed,
    dataAtendimento,
    skip: loadingAcompanhamentoPreNatal,
  })

  const {
    input: { value },
  } = usePecField({ name: meta.medicoesAnteriores })

  const medicoesAntropometricasPreNatal = useMemo(
    () => getMedicoesAntropometricasPreNatal(historicoMedicoes, tipoPreNatal),
    [historicoMedicoes, tipoPreNatal]
  )

  const converterParaMedicoesAntropometricasPreNatal = useMemo((): MedicaoAntropometricaPreNatal[] => {
    return value.map((item) => ({
      dataMedicao: Date.parse(item.data),
      peso: Number(item.peso),
      altura: Number(item.altura),
      origem: 'outroAtendimento',
    }))
  }, [value])

  const medicaoForImcReferencia = useMemo(() => {
    const dataValida = dataInicioGestacao instanceof Date && !Number.isNaN(dataInicioGestacao.getTime())
    const medicoesValidas = Array.isArray(medicoesAntropometricasPreNatal) && !medicoesAntropometricasPreNatal.isEmpty()

    return dataValida && medicoesValidas
      ? getMedicaoForImcReferencia(dataInicioGestacao, [
          ...medicoesAntropometricasPreNatal,
          ...converterParaMedicoesAntropometricasPreNatal,
        ])
      : undefined
  }, [dataInicioGestacao, medicoesAntropometricasPreNatal, converterParaMedicoesAntropometricasPreNatal])

  const imcReferencia = useMemo(() => {
    if (!medicaoForImcReferencia) return undefined

    const { peso, altura } = medicaoForImcReferencia
    if (peso != null && altura != null) {
      return calcularIMC(peso, altura)
    }

    return undefined
  }, [medicaoForImcReferencia])

  return (
    <>
      <ContentCardSection
        isLoading={loading || loadingPeriodoGestacional}
        messageLoading='Carregando dados das medições'
        canRender={canRender}
      >
        {!!imcReferencia && <ImcPreGestacionalCardItem imc={imcReferencia} />}
        <SubtitleCard title='De hoje' />
        <MedicoesListCard medicoes={medicoesHoje} cidadao={cidadao} gestacoes={gestacoes} isGestante={isGestante} />
        {medicoesHoje.length === 0 && <Text component='p'>Nenhuma medição registrada hoje.</Text>}

        <SubtitleCard title='Anteriores' />
        <MedicoesListCard
          medicoes={medicoesAnterioresExpandido}
          isListMedicaoAnterior
          cidadao={cidadao}
          gestacoes={gestacoes}
          isGestante={isGestante}
        />
        {medicoesAnterioresExpandido.length === 0 && (
          <Text component='p'>Nenhuma medição realizada em dias anteriores.</Text>
        )}
      </ContentCardSection>

      {medicoesAnterioresAccordion.length > 0 && (
        <AccordionCardSection
          id='MedicoesCard'
          title={{
            closed: `Mostrar + ${medicoesAnterioresAccordion.length} medições`,
            open: `Esconder medições`,
          }}
        >
          <MedicoesListCard
            medicoes={medicoesAnterioresAccordion}
            isListMedicaoAnterior
            cidadao={cidadao}
            gestacoes={gestacoes}
            isGestante={isGestante}
          />
          {medicoesAnterioresAccordion.length === 0 && (
            <Text component='p'>Nenhuma medição realizada em dias anteriores.</Text>
          )}
        </AccordionCardSection>
      )}
    </>
  )
}
